import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Star } from "lucide-react";
import { SEO } from "../components/SEO";
import { MaskedLines, FadeUp, Overline, GoldRule } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { SITE } from "../data/site";
import { SERVICES } from "../data/services";
import { TESTIMONIALS } from "../data/testimonials";

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Facials & Nano Needling in Orlando, FL | Lunar Ambitions"
        description="Lunar Ambitions is a licensed esthetician studio in Orlando, FL offering customized facials, nano needling, acne treatments, anti-aging skin care, and skin reset rituals. Book a personalized skin care treatment with a clinical-trained esthetician dedicated to your skin's long-term health."
      />

      <section
        ref={heroRef}
        className="relative min-h-[85vh] flex items-center overflow-hidden bg-white pt-52 lg:pt-72 pb-12"
        data-testid="home-hero"
      >
         <div className="absolute inset-x-0 top-0 lg:hidden" aria-hidden="true">
          <div className="relative w-full aspect-[3/4] overflow-hidden">
            <motion.img
              src="/images/facial-skin-care-treatment-lunar-ambitions-orlando.jpg"
              alt="Facial skin care treatment at Lunar Ambitions in Orlando FL"
              style={{ y: imgY }}
              className="absolute inset-0 w-full h-full object-cover object-[65%_50%]"
              data-testid="home-hero-image-mobile"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-white/75 to-white/40" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-white to-transparent" />
          </div>
        </div>

        <div className="hidden lg:block absolute inset-0" aria-hidden="true">
          <motion.img
            src="/images/facial-skin-care-treatment-lunar-ambitions-orlando.jpg"
            alt="Facial skin care treatment at Lunar Ambitions in Orlando FL"
            style={{ y: imgY }}
            className="absolute inset-0 w-full h-full object-cover object-[50%_100%]"
            data-testid="home-hero-image"
          />
          <div className="absolute inset-0 bg-[linear-gradient(to_left,transparent_0%,transparent_45%,#ffffff_78%)]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <div className="max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
            >
              <Overline data-testid="home-hero-overline">
                Lunar Ambitions · Orlando, FL
              </Overline>
            </motion.div>
            <h1
              className="mt-7 font-display font-normal tracking-tight leading-[1.04] text-navy text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem]"
              data-testid="home-hero-heading"
            >
              <MaskedLines lines={["Radiant Skin", "Starts Here"]} />
            </h1>
            <FadeUp delay={0.55}>
              <p
                className="mt-7 max-w-md text-base sm:text-lg text-navy/70 leading-relaxed font-light"
                data-testid="home-hero-subheading"
              >
                Personalized Facials, Nano Needling, and Dermaplaning in
                Orlando, FL
              </p>
            </FadeUp>
            <FadeUp delay={0.7}>
              <div className="mt-10 flex flex-wrap items-center gap-6">
                <a
                  href={SITE.bookingConsultation}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-hero-free-consultation-button"
                  className="group inline-flex items-center gap-3 bg-navy text-white text-sm tracking-[0.18em] uppercase font-medium px-9 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
                >
                  Book Free Consultation
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>



      <Marquee
        items={[
          "Facials",
          "Nano Needling",
          "Dermaplaning",
          "Nano Infusion",
          "Skin Renewal",
          "Chemical Peel",
        ]}
      />

      <section
        className="pt-14 sm:pt-32 pb-24 sm:pb-32 bg-white"
        data-testid="home-esthetician-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-0 lg:gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="hidden lg:block">
              <FadeUp>
                <div className="relative max-w-md">
                  <div
                    aria-hidden="true"
                    className="absolute -inset-3 border border-gold/40 -translate-x-4 translate-y-4"
                  />
                  <div className="relative overflow-hidden aspect-[1027/1540]">
                    <img
                      src="/images/alena-esthetician-orlando.jpg"
                      alt="Alena, licensed esthetician and owner of Lunar Ambitions in Orlando FL, holding a facial massage tool and skin care product in her professional treatment studio."
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                      data-testid="home-esthetician-image"
                    />
                  </div>
                </div>
              </FadeUp>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <FadeUp>
              <h2
                className="font-display italic text-4xl sm:text-5xl text-gold/80"
                data-testid="home-esthetician-label"
              >
                Meet Your Esthetician
              </h2>
            </FadeUp>
            <div className="lg:hidden mt-8">
              <FadeUp>
                <div className="relative max-w-md">
                  <div
                    aria-hidden="true"
                    className="absolute -inset-3 border border-gold/40 -translate-x-4 translate-y-4"
                  />
                  <div className="relative overflow-hidden aspect-[1027/1540]">
                    <img
                      src="/images/alena-esthetician-orlando.jpg"
                      alt="Alena, licensed esthetician and owner of Lunar Ambitions in Orlando FL, holding a facial massage tool and skin care product in her professional treatment studio."
                      className="w-full h-full object-cover"
                      data-testid="home-esthetician-image-mobile"
                    />
                  </div>
                </div>
              </FadeUp>
            </div>
            <FadeUp delay={0.1}>
              <p
                className="mt-14 lg:mt-8 text-base sm:text-lg text-navy/75 leading-relaxed font-light max-w-xl"
                data-testid="home-esthetician-bio"
              >
                Hi, I’m Alena, a licensed esthetician based in Orlando, FL,
                originally from Kiev, Ukraine, where high standards of skin care
                are deeply ingrained in the culture. I bring that same
                dedication to every client at Lunar Ambitions, combining
                advanced technique with a personal, intuitive approach. I
                believe great skin care is both science and intuition, and that
                every client deserves a treatment built specifically around
                their skin, their goals, and their comfort.
              </p>
              <p className="mt-6 text-base sm:text-lg text-navy/75 leading-relaxed font-light max-w-xl">
                I look forward to meeting you and offering you the best quality
                care possible.
              </p>
              <p className="mt-8 font-display italic text-2xl text-navy">
                — Alena
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <GoldRule className="mt-10 w-24" />
              <Link
                to="/about"
                data-testid="home-esthetician-about-link"
                className="group mt-8 inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase font-medium text-navy hover:text-gold transition-colors duration-300"
              >
                About
                <ArrowRight
                  size={15}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <section
        className="pt-14 sm:pt-32 pb-24 sm:pb-32 bg-[#F9F8F6]"
        data-testid="home-services-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between gap-8 flex-wrap">
            <div>
              <FadeUp>
                <h2
                  className="font-display italic text-4xl sm:text-5xl text-gold/80"
                  data-testid="home-services-label"
                >
                  Service Options
                </h2>
              </FadeUp>
            </div>
            <FadeUp delay={0.15}>
              <a
                href={SITE.bookingGeneral}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-services-book-now-button"
                className="group inline-flex items-center gap-2 border border-navy/20 text-navy text-xs tracking-[0.18em] uppercase font-medium px-7 py-3.5 transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                Book Now
                <ArrowUpRight
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
            </FadeUp>
          </div>

          <div className="mt-14 relative">
            <ul className="divide-y divide-navy/10 border-y border-navy/10">
              {SERVICES.map((service, i) => (
                <FadeUp key={service.slug} delay={Math.min(i * 0.04, 0.3)}>
                  <li>
                    <Link
                      to={service.slug}
                      data-testid={`home-service-${service.slug.slice(1)}`}
                      className="group flex items-center gap-5 sm:gap-8 py-5 sm:py-6 transition-colors duration-300 hover:bg-white px-2 sm:px-4 -mx-2 sm:-mx-4"
                    >
                      <span className="font-display italic text-lg text-gold/80 w-8 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-display text-xl sm:text-2xl text-navy leading-snug transition-colors duration-300 group-hover:text-gold">
                          {service.name}
                        </span>
                        <span className="mt-1 block text-xs tracking-[0.14em] uppercase text-navy/50">
                          {service.durationShort} · {service.price}
                        </span>
                      </span>
                      <ArrowUpRight
                        size={20}
                        className="shrink-0 text-navy/30 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </Link>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden bg-[#F5F1E8] py-16 sm:py-24"
        data-testid="home-consultation-cta-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <FadeUp>
                <Overline as="h2" data-testid="home-consultation-cta-label">
                  Free Consultation
                </Overline>
                <p
                  className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight leading-[1.1]"
                  data-testid="home-consultation-cta-heading"
                >
                  Not sure which treatment is right for your skin?
                </p>
                <p
                  className="mt-5 text-base sm:text-lg text-navy/70 leading-relaxed font-light max-w-xl"
                  data-testid="home-consultation-cta-text"
                >
                  Book a complimentary consultation in Orlando, FL and we'll
                  review your skin together before you commit to a service.
                </p>
              </FadeUp>
            </div>
            <div className="lg:col-span-5 lg:justify-self-end">
              <FadeUp delay={0.15}>
                <a
                  href={SITE.bookingConsultation}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-consultation-cta-button"
                  className="group inline-flex items-center gap-3 bg-navy text-white text-sm tracking-[0.18em] uppercase font-semibold px-10 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
                >
                  Book Free Consultation
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden bg-navy pt-14 sm:pt-32 pb-24 sm:pb-32"
        data-testid="home-testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <h2
              className="font-display italic text-4xl sm:text-5xl text-gold/80"
              data-testid="home-testimonials-label"
            >
              Client Love
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-12 lg:grid-cols-3 lg:gap-14">
            {TESTIMONIALS.map((testimonial, i) => (
              <FadeUp key={testimonial.name} delay={Math.min(i * 0.1, 0.3)}>
                <div
                  className="h-full border-t border-gold/30 pt-8"
                  data-testid={`home-testimonial-${testimonial.name
                    .toLowerCase()
                    .replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="flex gap-1" aria-hidden="true">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star
                        key={s}
                        size={14}
                        className="text-gold fill-gold"
                      />
                    ))}
                  </div>
                  <p className="mt-6 text-white/80 text-base leading-relaxed font-light">
                    “{testimonial.quote}”
                  </p>
                  <p className="mt-6 font-display italic text-lg text-gold/90">
                    — {testimonial.name}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section
        className="pt-16 sm:pt-32 pb-24 sm:pb-32 bg-white"
        data-testid="home-gift-card-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <FadeUp>
              <h2
                className="font-display italic text-4xl sm:text-5xl text-gold/80"
                data-testid="home-giftcard-label"
              >
                Gift Card
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p
                className="mt-6 text-base sm:text-lg text-navy/75 leading-relaxed font-light max-w-lg"
                data-testid="home-gift-card-text"
              >
                Give the gift of healthy skin. Lunar Ambitions gift cards are
                redeemable for facials, nano needling, dermaplaning, and skin
                care treatments in Orlando, FL.
              </p>
              <a
                href={SITE.giftOrder}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-gift-card-buy-button"
                className="group mt-9 inline-flex items-center gap-3 bg-navy text-white text-sm tracking-[0.18em] uppercase font-medium px-9 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
              >
                Buy Gift Card
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
            </FadeUp>
          </div>
          <div className="lg:col-span-6">
            <FadeUp delay={0.15}>
              <div className="relative max-w-lg mx-auto">
                <div
                  aria-hidden="true"
                  className="absolute -inset-3 border border-gold/40 translate-x-4 -translate-y-4"
                />
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src="/images/lunar-ambitions-gift-card.jpg"
                    alt="A navy blue gift card with gold logo and text reading 'Lunar Ambitions Gift Card.' The card is decorated with gold celestial symbols and is surrounded by white roses, small white flowers, gold ribbon, and a gold dish, all on a dark blue background with gold sprinkles."
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    data-testid="home-gift-card-image"
                  />
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden bg-[#F5F1E8] py-24 sm:py-32"
        data-testid="home-cta-section"
      >
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <FadeUp>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]"
              data-testid="home-cta-heading"
            >
              Radiant Skin Starts Here
            </h2>
            <p className="mt-6 text-navy/70 text-base sm:text-lg font-light max-w-xl mx-auto">
              Personalized Facials, Nano Needling, and Dermaplaning in Orlando,
              FL
            </p>
            <a
              href={SITE.bookingGeneral}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-cta-book-now-button"
              className="group mt-10 inline-flex items-center gap-3 bg-navy text-white text-sm tracking-[0.18em] uppercase font-semibold px-10 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
            >
              Book Now
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Home;
